// src/components/Products.js

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faEdit, faUpload,faFileCsv, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import '../css/Products.css';


const Products = () => {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

    const [newProduct, setNewProduct] = useState({
        name: '',
        category: '',
        attributes: {},
    });
    const [newAttributeName, setNewAttributeName] = useState('');
    const [newAttributeValue, setNewAttributeValue] = useState('');
    const [newImageFile, setNewImageFile] = useState(null);
    const [newImageUrl, setNewImageUrl] = useState('');

    const [editProduct, setEditProduct] = useState(null);
    const [editAttributeName, setEditAttributeName] = useState('');
    const [editAttributeValue, setEditAttributeValue] = useState('');
    const [editImageFile, setEditImageFile] = useState(null);
    const [editImageUrl, setEditImageUrl] = useState('');

    useEffect(() => {
        fetchProducts();
        fetchCategories();
    }, []);

    const fetchProducts = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/products`);
            setProducts(response.data);
        } catch (error) {
            setError('Failed to fetch products');
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/categories`);
            setCategories(response.data);
        } catch (error) {
            setError('Failed to fetch categories');
        }
    };

    const handleAddProduct = async (e) => {
        e.preventDefault();
        setError('');
        setSuccessMessage('');

        try {
            const token = localStorage.getItem('token');
            const formData = new FormData();
            formData.append('name', newProduct.name);
            formData.append('category', newProduct.category);
            formData.append('attributes', JSON.stringify(newProduct.attributes));

            if (newImageFile) {
                formData.append('image_file', newImageFile);
            } else if (newImageUrl) {
                formData.append('image_url', newImageUrl);
            }

            const response = await axios.post(
                `${API_BASE_URL}/admin/products`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            setSuccessMessage(response.data.message);
            setNewProduct({ name: '', category: '', attributes: {} });
            setNewAttributeName('');
            setNewAttributeValue('');
            setNewImageFile(null);
            setNewImageUrl('');
            fetchProducts();
        } catch (error) {
            if (error.response) {
                setError(error.response.data.message || 'Failed to add product');
            } else {
                setError('An unexpected error occurred');
            }
        }
    };

    const handleNewAttributeAdd = () => {
        if (newAttributeName && newAttributeValue) {
            setNewProduct((prev) => ({
                ...prev,
                attributes: {
                    ...prev.attributes,
                    [newAttributeName]: newAttributeValue,
                },
            }));
            setNewAttributeName('');
            setNewAttributeValue('');
        }
    };

    const handleEditProduct = (product) => {
        setEditProduct({
            ...product,
            removeAttributes: [],
        });
        setEditAttributeName('');
        setEditAttributeValue('');
        setEditImageFile(null);
        setEditImageUrl('');
        setError('');
        setSuccessMessage('');
    };

    const handleEditAttributeAdd = () => {
        if (editAttributeName && editAttributeValue) {
            setEditProduct((prev) => ({
                ...prev,
                attributes: {
                    ...prev.attributes,
                    [editAttributeName]: editAttributeValue,
                },
            }));
            setEditAttributeName('');
            setEditAttributeValue('');
        }
    };

    const handleRemoveAttribute = (attrName) => {
        setEditProduct((prev) => {
            const newAttributes = { ...prev.attributes };
            delete newAttributes[attrName];
            return {
                ...prev,
                attributes: newAttributes,
                removeAttributes: [...prev.removeAttributes, attrName],
            };
        });
    };

    const saveEditedProduct = async () => {
        try {
            const token = localStorage.getItem('token');
            const formData = new FormData();
            formData.append('name', editProduct.name);
            formData.append('category', editProduct.category);
            formData.append('attributes', JSON.stringify(editProduct.attributes));
            formData.append('remove_attributes', JSON.stringify(editProduct.removeAttributes));

            if (editImageFile) {
                formData.append('image_file', editImageFile);
            } else if (editImageUrl) {
                formData.append('image_url', editImageUrl);
            }

            await axios.put(
                `${API_BASE_URL}/admin/products/${editProduct.id}`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );
            setSuccessMessage('Product updated successfully');
            setEditProduct(null);
            fetchProducts();
        } catch (error) {
            if (error.response) {
                setError(error.response.data.message || 'Failed to update product');
            } else {
                setError('An unexpected error occurred');
            }
        }
    };

    const handleDeleteProduct = async (productId) => {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API_BASE_URL}/admin/products/${productId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setSuccessMessage('Product deleted successfully');
            fetchProducts();
        } catch (error) {
            if (error.response) {
                setError(error.response.data.message || 'Failed to delete product');
            } else {
                setError('An unexpected error occurred');
            }
        }
    };

    const handleEditAttributeChange = (key, value) => {
        setEditProduct((prev) => ({
            ...prev,
            attributes: {
                ...prev.attributes,
                [key]: value,
            },
        }));
    };

    const downloadCSV = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_BASE_URL}/export_products_csv`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'products.csv');
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error('Error downloading CSV:', error);
        }
    };

    const downloadExcel = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_BASE_URL}/export_products_excel`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'products.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error('Error downloading Excel:', error);
        }
    };
    

    return (
        <div className="products-container">
            <h2>Manage Products</h2>
            <div className="export-buttons">
                <button onClick={downloadCSV}>
                    <FontAwesomeIcon icon={faFileCsv} /> Download CSV
                </button>
                <button onClick={downloadExcel}>
                    <FontAwesomeIcon icon={faFileExcel} /> Download Excel
                </button>
            </div>
            {error && <p className="error">{error}</p>}
            {successMessage && <p className="success">{successMessage}</p>}

            {/* Add Product Form */}
            <form onSubmit={handleAddProduct} className="product-form">
                <h3>
                    <FontAwesomeIcon icon={faPlus} /> Add New Product
                </h3>
                <input
                    type="text"
                    placeholder="Product Name"
                    value={newProduct.name}
                    onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                    required
                />
                <select
                    value={newProduct.category}
                    onChange={(e) => setNewProduct({ ...newProduct, category: e.target.value })}
                    required
                >
                    <option value="">Select Category</option>
                    {categories.map((cat, index) => (
                        <option key={index} value={cat}>
                            {cat}
                        </option>
                    ))}
                </select>

                {/* Dynamic Attributes for New Product */}
                <div className="attributes-section">
                    <h4>Attributes</h4>
                    {Object.entries(newProduct.attributes).map(([key, value], index) => (
                        <p key={index}>
                            {key}: {value}
                        </p>
                    ))}
                    <div className="attribute-input">
                        <input
                            type="text"
                            placeholder="Attribute Name"
                            value={newAttributeName}
                            onChange={(e) => setNewAttributeName(e.target.value)}
                        />
                        <input
                            type="text"
                            placeholder="Attribute Value"
                            value={newAttributeValue}
                            onChange={(e) => setNewAttributeValue(e.target.value)}
                        />
                        <button type="button" onClick={handleNewAttributeAdd}>
                            <FontAwesomeIcon icon={faPlus} /> Add Attribute
                        </button>
                    </div>
                </div>

                {/* Image Upload */}
                <div className="image-upload">
                    <h4>Product Image</h4>
                    <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setNewImageFile(e.target.files[0])}
                    />
                    <p>Or</p>
                    <input
                        type="text"
                        placeholder="Image URL"
                        value={newImageUrl}
                        onChange={(e) => setNewImageUrl(e.target.value)}
                    />
                </div>

                <button type="submit">
                    <FontAwesomeIcon icon={faUpload} /> Add Product
                </button>
            </form>

            {/* Product List */}
            <h3>Product List</h3>
            <div className="product-cards">
    {products.map((product) => (
        <div key={product.id} className="product-card">
            <div className="product-header">
                <h4>{product.name}</h4>
                <span>{product.category}</span>
            </div>
            <div className="product-body">
                <p><strong>Qty:</strong> {product.attributes.quantity || 'N/A'}</p>
                {Object.entries(product.attributes || {})
                    .filter(([key]) => key !== 'quantity')
                    .map(([key, value], index) => (
                        <p key={index}>{key}: {value}</p>
                    ))}
                {product.image && (
                    <img src={product.image} alt={product.name} />
                )}
            </div>
            <div className="product-footer">
                <button className="edit-button" onClick={() => handleEditProduct(product)}>
                    <FontAwesomeIcon icon={faEdit} /> Edit
                </button>
                <button className="delete-button" onClick={() => handleDeleteProduct(product.id)}>
                    <FontAwesomeIcon icon={faTrash} /> Delete
                </button>
            </div>
        </div>
    ))}
</div>


            {/* Edit Product Modal */}
            {editProduct && (
                <div className="edit-product-modal">
                    <div className="modal-content">
                        <button className="close-button" onClick={() => setEditProduct(null)}>
                            &times;
                        </button>
                        <h3>Edit Product</h3>
                        <input
                            type="text"
                            name="name"
                            placeholder="Product Name"
                            value={editProduct.name}
                            onChange={(e) => setEditProduct({ ...editProduct, name: e.target.value })}
                        />
                        <select
                            name="category"
                            value={editProduct.category}
                            onChange={(e) => setEditProduct({ ...editProduct, category: e.target.value })}
                        >
                            <option value="">Select Category</option>
                            {categories.map((cat, index) => (
                                <option key={index} value={cat}>
                                    {cat}
                                </option>
                            ))}
                        </select>

                        {/* Edit Attributes */}
                        <div className="attributes-section">
                            <h4>Attributes</h4>
                            {Object.entries(editProduct.attributes || {})
                                .filter(([key]) => key !== 'quantity')
                                .map(([key, value], index) => (
                                    <div key={index} className="attribute-item">
                                        <input type="text" value={key} readOnly />
                                        <input
                                            type="text"
                                            value={value}
                                            onChange={(e) => handleEditAttributeChange(key, e.target.value)}
                                        />
                                        <button type="button" onClick={() => handleRemoveAttribute(key)}>
                                            <FontAwesomeIcon icon={faTrash} /> Remove
                                        </button>
                                    </div>
                                ))}
                            {/* Add new attribute */}
                            <div className="attribute-input">
                                <input
                                    type="text"
                                    placeholder="Attribute Name"
                                    value={editAttributeName}
                                    onChange={(e) => setEditAttributeName(e.target.value)}
                                />
                                <input
                                    type="text"
                                    placeholder="Attribute Value"
                                    value={editAttributeValue}
                                    onChange={(e) => setEditAttributeValue(e.target.value)}
                                />
                                <button type="button" onClick={handleEditAttributeAdd}>
                                    <FontAwesomeIcon icon={faPlus} /> Add Attribute
                                </button>
                            </div>
                        </div>

                        {/* Image Upload */}
                        <div className="image-upload">
                            <h4>Product Image</h4>
                            <input type="file" accept="image/*" onChange={(e) => setEditImageFile(e.target.files[0])} />
                            <p>Or</p>
                            <input
                                type="text"
                                placeholder="Image URL"
                                value={editImageUrl}
                                onChange={(e) => setEditImageUrl(e.target.value)}
                            />
                            {editProduct.image && (
                                <div>
                                    <p>Current Image:</p>
                                    <img src={editProduct.image} alt="Product" width="100" />
                                </div>
                            )}
                        </div>

                        <button className="save-button" onClick={saveEditedProduct}>
                            Save Changes
                        </button>
                        <button className="cancel-button" onClick={() => setEditProduct(null)}>
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Products;